import type {
  CustomSeriesRenderItemAPI,
  CustomSeriesRenderItemParams,
  CustomSeriesRenderItemReturn,
  DefaultLabelFormatterCallbackParams,
  EChartsOption,
  TooltipComponentFormatterCallbackParams,
} from "echarts";

import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { escape, isMatch } from "lodash";

import type {
  PlotData,
  ReferenceLine,
  TimeSeriesPoint,
  TimeSeriesTrace,
} from "../../../api";

import { ThresholdTypes } from "../../../api";
import { sanitizeRichText } from "../../../components/ui-kit/sanitize-rich-text";
import { ppgConditionText } from "../../../constants/enum-to-text";
import { formatChartDate, formatChartDateTime } from "./heartcore-format";

// --- Design-token bridge ---------------------------------------------------
// ECharts writes SVG attributes and can't consume `var(--foo)`, so resolve
// tokens to computed values at module load, falling back to the given hex if
// the CSS hasn't loaded (bare test env).
//
// oklch()/oklab() are rejected: ECharts' hover pipeline doesn't round-trip
// CSS Color 4 syntax and the line goes transparent on hover. Tailwind v4
// emits its palette tokens in oklch, so those fall back to the hex; the
// Seerlinq brand tokens chain through hex ramps and still resolve.
function cssVar(name: string, fallback: string): string {
  if (typeof document === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  if (!v) return fallback;
  if (v.startsWith("oklch") || v.startsWith("oklab")) return fallback;
  return v;
}

// True when the app is in dark mode (root html has the `.dark` class — see
// AppModel.setTheme). Read at every access so chart options rebuilt on a
// theme toggle pick up the correct ink values.
function isDarkMode(): boolean {
  return (
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark")
  );
}

// Chart palette anchored to css/main.css tokens where they cleanly map;
// the rest are Tailwind categorical hues without a semantic token.
export const CHART_COLORS = {
  brandPrimary: cssVar("--brand-primary", "#bb16a3"),
  brandSecondary: cssVar("--brand-secondary", "#05c3de"),
  alert: cssVar("--alert-solid", "#ea580c"),
  danger: cssVar("--danger-solid", "#dc2626"),
  neutralMuted: cssVar("--color-gray-400", "#9ca3af"),
  symptomsBlue: cssVar("--color-blue-500", "#3b82f6"),
  symptomsPurple: cssVar("--color-purple-500", "#a855f7"),
  symptomsCyan: cssVar("--color-cyan-400", "#22d3ee"),
  bpPink: cssVar("--color-pink-500", "#e91e8c"),
  thresholdCautionLight: cssVar("--color-fuchsia-400", "#e879f9"),
  thresholdCautionDark: cssVar("--color-fuchsia-700", "#a21caf"),
  thresholdNeutral: cssVar("--color-gray-600", "#4b5563"),
  // Consecutive-run triangles on the rolling metric line, matching the
  // QuickLook's purple marker. One colour for both directions — the triangle
  // points the way the run goes, so the hue stays the marker's identity.
  trendRunPurple: cssVar("--color-purple-600", "#9333ea"),
  // Theme-flipping ink shades. Getters, not constants, so options rebuilt on
  // a theme toggle pick up the current value.
  get arrowInk() {
    return isDarkMode() ? "#e6e6e6" : "#111111";
  },
  get gridInk() {
    return isDarkMode() ? "#d0d0d0" : "#333333";
  },
  get rulerInk() {
    return isDarkMode() ? "#a5a5a5" : "#555555";
  },
  // Tooltip surface (bg + border + text) also flips with the theme so the
  // popup reads on top of dark app chrome without a jarring white flash.
  get tooltipBg() {
    return isDarkMode() ? "#1f1f22" : "#ffffff";
  },
  get tooltipBorder() {
    return isDarkMode() ? "#3f3f45" : "#dcdce0";
  },
  get tooltipText() {
    return isDarkMode() ? "#e6e6e6" : "#111111";
  },
};

// A quieter sibling of a colour: same hue, washed toward the page background,
// so a secondary line reads as belonging to its primary without competing with
// it. Mixing toward the background rather than always lightening keeps it
// working on both themes, and keeps near-black/near-white inks from collapsing
// into the background they're mixed with.
//
// Every colour this is handed today is hex (cssVar resolves oklch to its hex
// fallback), so the miss below is defensive. It matters anyway: mixHex returns
// its input untouched when it can't parse, which would hand the secondary line
// its primary's exact colour — two indistinguishable lines, the very collision
// the muting exists to prevent. A neutral grey is a worse sibling but an honest
// one, and it can't be confused with a categorical hue.
function mutedShade(color: string): string {
  const mixed = mixHex(color, isDarkMode() ? "#000000" : "#ffffff", 0.45);
  return mixed === color ? CHART_COLORS.neutralMuted : mixed;
}

// Linear mix of two #rgb / #rrggbb colours, `t` of the way from `a` to `b`.
// Anything unparseable comes back as `a`, which callers must treat as a miss
// rather than a result — see mutedShade.
function mixHex(a: string, b: string, t: number): string {
  const from = parseHex(a);
  const to = parseHex(b);
  if (!from || !to) return a;
  return `#${from
    .map((channel, i) =>
      Math.round(channel + (to[i] - channel) * t)
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")}`;
}

function parseHex(color: string): [number, number, number] | null {
  const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim());
  if (!match) return null;
  const digits =
    match[1].length === 3 ? match[1].replace(/./g, (c) => c + c) : match[1];
  return [
    parseInt(digits.slice(0, 2), 16),
    parseInt(digits.slice(2, 4), 16),
    parseInt(digits.slice(4, 6), 16),
  ];
}

// Tooltip surface styling. A factory, not a constant — the theme getters
// above only fire on property access.
function tooltipTheme(extraCss = "") {
  return {
    backgroundColor: CHART_COLORS.tooltipBg,
    borderColor: CHART_COLORS.tooltipBorder,
    borderWidth: 1,
    textStyle: { color: CHART_COLORS.tooltipText },
    extraCssText: "box-shadow: 0 4px 12px rgba(0,0,0,0.15);" + extraCss,
  };
}

// Tooltip formatters return raw HTML that ECharts injects via innerHTML, so
// all user/backend text must go through lodash `escape` — a stored payload
// would otherwise execute on hover. Per-point formatters route through
// tooltipHtml() so the escaping lives in one place.

// `glyphHtml` is caller-built trusted markup; name and valueText are data
// and get escaped here.
function tooltipHtml(
  dateInput: string | number,
  glyphHtml: string,
  name: string,
  valueText: string,
): string {
  return [
    `<strong>${formatChartDateTime(dateInput)}</strong>`,
    `${glyphHtml}${escape(name)}: <strong>${escape(valueText)}</strong>`,
  ].join("<br/>");
}

// Round tooltip dot in the series colour — the default glyph.
function tooltipDot(color: string): string {
  return `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${color};margin-right:4px;"></span>`;
}

// Numbers are fixed-decimal; anything else (malformed report data) is
// stringified — tooltipHtml escapes it before it reaches innerHTML.
function tooltipValue(v: unknown, decimals: number): string {
  return typeof v === "number" ? v.toFixed(decimals) : String(v);
}

// event_color_hex lands inside an inline style attribute — accept only hex
// colours so a malformed/malicious value can't break out of the attribute.
function safeHexColor(color: string): string {
  return /^#[0-9a-f]{3,8}$/i.test(color) ? color : CHART_COLORS.neutralMuted;
}

// Threshold/reference lines draw beneath the data: data series sit at z 1–3,
// the AFib risk-fill at z -1, so z 0 is under every line but above the fill.
const THRESHOLD_Z = 0;

// Carrier series holding a chart's reference-line markLines. Kept off the
// data series so toggling one line can't take the limits with it — every stat
// row lists this in `seriesNames` and ChartSection ORs the flags, so the
// limits disappear only once every line is hidden.
export const REF_LINES_SERIES = "Reference lines";

// Extract reference lines that apply to the whole plot (or to a specific
// trace when `facetFilter` is passed). BE convention: a line with a null
// `applies_to` is plot-wide; a filter object matches every facet key/value.
function getReferenceLines(
  plot: PlotData | null,
  facetFilter?: Record<string, string>,
): ReferenceLine[] {
  const lines = plot?.reference_lines ?? [];
  return lines.filter(
    (rl) =>
      !rl.applies_to || (!!facetFilter && isMatch(facetFilter, rl.applies_to)),
  );
}

// Returns undefined (not an empty markLine) when there are no lines.
function referenceLinesMark(
  lines: ReferenceLine[],
  color: string,
  labelFormatter: (rl: ReferenceLine) => string,
) {
  if (lines.length === 0) return undefined;
  return {
    silent: true,
    symbol: "none" as const,
    z: THRESHOLD_Z, // render beneath the data lines
    lineStyle: {
      type: "dashed" as const,
      color,
      width: 2,
      opacity: 0.6,
    },
    label: { color, lineHeight: 12 },
    data: lines.map((rl) => ({
      yAxis: rl.value,
      label: { formatter: labelFormatter(rl) },
    })),
  };
}

// Facet matching instead of label matching: the BE prepends patient id into
// `label`, so e.g. "Raw DRI" never appears there verbatim.
function findTraceByFacets(
  traces: TimeSeriesTrace[],
  matcher: Record<string, string>,
): TimeSeriesTrace | undefined {
  return traces.find((t) => isMatch(t.facets ?? {}, matcher));
}

// Convenience: same as findTraceByFacets but returns the points array (or []).
export function pointsByFacets(
  traces: TimeSeriesTrace[],
  matcher: Record<string, string>,
): TimeSeriesPoint[] {
  return findTraceByFacets(traces, matcher)?.points ?? [];
}

// Quality flag → raw-dot colour. Unknown flags read as danger, not OK, so a
// future flag value can't silently render as good.
function qualityFlagColor(flag: string | number | undefined): string {
  const s = typeof flag === "string" ? flag.toLowerCase() : "";
  if (!s) return CHART_COLORS.neutralMuted;
  if (s === "ok" || s === "good") return CHART_COLORS.neutralMuted;
  if (s.includes("low") || s.includes("warning") || s.includes("poor"))
    return CHART_COLORS.alert;
  return CHART_COLORS.danger;
}

// Backend-driven event markers (event_shape / event_color_hex). One union
// drives all lookup tables below so a new shape forces a compile-time update.
export type EventShape =
  | "circle"
  | "square"
  | "diamond"
  | "triangle-up"
  | "triangle-down"
  | "star"
  | "cross"
  | "pentagon"
  | "arrow-bar-right"
  | "hourglass"
  | "asterisk"
  | "x";

export type ChartEvent = {
  // When the event happened (the API's `event_date`, normalised to local noon
  // by toChartEvent) — drives x-position, sorting and clustering. Deliberately
  // not the record's creation timestamp.
  date: string;
  category: string; // resolved display label
  subLine: string; // resolved "type | details" line ("" when none)
  description?: string;
  colorHex: string; // event_color_hex
  shape: EventShape; // event_shape name
};

// SVG path symbols for shapes ECharts has no built-in for. Coordinates use a
// 0–10 box; ECharts scales the path to the symbol size.
const STAR_PATH =
  "path://M5,0 L6.18,3.38 L9.76,3.45 L6.90,5.62 L7.94,9.05 L5,7 L2.06,9.05 L3.10,5.62 L0.24,3.45 L3.82,3.38 Z";
const HOURGLASS_PATH = "path://M0,0 L10,0 L5,5 L10,10 L0,10 L5,5 Z";
const CROSS_PATH =
  "path://M2,0 L5,3 L8,0 L10,2 L7,5 L10,8 L8,10 L5,7 L2,10 L0,8 L3,5 L0,2 Z";
const TRIANGLE_DOWN_PATH = "path://M0,0 L10,0 L5,10 Z";
const PENTAGON_PATH = "path://M5,0 L9.51,3.45 L7.8,9.51 L2.2,9.51 L0.49,3.45 Z";
const ARROW_RIGHT_PATH = "path://M0,3.5 L6,3.5 L6,1 L10,5 L6,9 L6,6.5 L0,6.5 Z";
const ASTERISK_PATH =
  "path://M5,0 L5.9,3.44 L9.33,2.5 L6.8,5 L9.33,7.5 L5.9,6.56 L5,10 L4.1,6.56 L0.67,7.5 L3.2,5 L0.67,2.5 L4.1,3.44 Z";
// A diagonal cross (the plus rotated 45°).
const X_PATH =
  "path://M1.06,0 L5,3.94 L8.94,0 L10,1.06 L6.06,5 L10,8.94 L8.94,10 L5,6.06 L1.06,10 L0,8.94 L3.94,5 L0,1.06 Z";

// Backend event_shape name → ECharts scatter symbol (built-in name or path).
const EVENT_SHAPE_SYMBOL: Record<EventShape, string> = {
  circle: "circle",
  square: "rect",
  diamond: "diamond",
  "triangle-up": "triangle",
  "triangle-down": TRIANGLE_DOWN_PATH,
  star: STAR_PATH,
  cross: CROSS_PATH,
  pentagon: PENTAGON_PATH,
  "arrow-bar-right": ARROW_RIGHT_PATH,
  hourglass: HOURGLASS_PATH,
  asterisk: ASTERISK_PATH,
  x: X_PATH,
};

// Backend event_shape name → tooltip glyph (mirrors the monitoring EventMarker).
const EVENT_SHAPE_GLYPH: Record<EventShape, string> = {
  circle: "●",
  square: "■",
  diamond: "◆",
  "triangle-up": "▲",
  "triangle-down": "▼",
  star: "★",
  cross: "✚",
  pentagon: "⬟",
  "arrow-bar-right": "↦",
  hourglass: "⧗",
  asterisk: "*",
  x: "✕",
};

// Own-property checks (not plain indexing with a ?? fallback): `shape` is a
// backend string cast to EventShape, so a hostile/buggy value like "toString"
// would otherwise dredge up an Object.prototype member instead of falling
// back to the default.
function eventShapeSymbol(shape: EventShape): string {
  return EVENT_SHAPE_SYMBOL.hasOwnProperty(shape)
    ? EVENT_SHAPE_SYMBOL[shape]
    : "circle";
}

function eventGlyph(shape: EventShape): string {
  return EVENT_SHAPE_GLYPH.hasOwnProperty(shape)
    ? EVENT_SHAPE_GLYPH[shape]
    : "●";
}

// ---------------------------------------------------------------------------
// Events bar (timeline of clinical events as scatter symbols on one row)
// ---------------------------------------------------------------------------

function eventRowHtml(e: ChartEvent): string {
  const dateStr = formatChartDate(e.date);
  // Muted grey shades on both themes: rulerInk matches the axis-pointer line,
  // neutralMuted is a step lighter (used for date + description).
  const mutedStrong = CHART_COLORS.rulerInk;
  const mutedWeak = CHART_COLORS.neutralMuted;
  const icon = `<span style="color:${safeHexColor(e.colorHex)};margin-right:4px;font-size:12px;line-height:1;">${eventGlyph(e.shape)}</span>`;
  const line1 = `<div style="display:flex;align-items:center;gap:2px;line-height:1.8;">${icon}<span>${escape(e.category)}</span><span style="color:${mutedWeak};font-size:10px;margin-left:6px;">${dateStr}</span></div>`;
  const indent = "padding-left:16px;font-size:11px;line-height:1.4;";
  const line2 = e.subLine
    ? `<div style="${indent}color:${mutedStrong};">${escape(e.subLine)}</div>`
    : "";
  // The description is rich text: render its markup (sanitized — this lands in
  // innerHTML) instead of escaping the tags into view. `prose` styles the tags;
  // the margin reset keeps a <p>-per-line description from ballooning the
  // tooltip, and --tw-prose-* pins the ramp to the tooltip's muted ink.
  const line3 = e.description
    ? `<div class="prose prose-sm **:my-0" style="${indent}--tw-prose-body:${mutedWeak};--tw-prose-bold:${mutedStrong};--tw-prose-bullets:${mutedWeak};--tw-prose-counters:${mutedWeak};color:${mutedWeak};margin-bottom:3px;">${sanitizeRichText(e.description)}</div>`
    : "";
  return line1 + line2 + line3;
}

export function buildEventsOption(
  events: ChartEvent[],
  timeRange: { min: string; max: string } | null,
  zoomRange: { start: number; end: number } | null = null,
): EChartsOption {
  // Cluster events that would land within ~24 px of each other at the
  // current zoom level (assuming ~700 px of chart draw area).
  const totalTimeMs = timeRange
    ? new Date(timeRange.max).getTime() - new Date(timeRange.min).getTime()
    : 365 * 86_400_000;
  const zoomFraction = Math.max(
    ((zoomRange?.end ?? 100) - (zoomRange?.start ?? 0)) / 100,
    0.005,
  );
  const thresholdMs = (24 / 700) * totalTimeMs * zoomFraction;

  const sorted = [...events].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );

  // Greedy grouping: an event joins the current group while it stays within
  // thresholdMs of the group's first event; clusters anchor at the mid-time.
  const groups: ChartEvent[][] = [];
  for (const ev of sorted) {
    const group = groups[groups.length - 1];
    const t = new Date(ev.date).getTime();
    if (group && t - new Date(group[0].date).getTime() <= thresholdMs) {
      group.push(ev);
    } else {
      groups.push([ev]);
    }
  }
  const clusters = groups.map((group) => ({
    centerMs:
      (new Date(group[0].date).getTime() +
        new Date(group[group.length - 1].date).getTime()) /
      2,
    events: group,
  }));

  // Singleton events: one scatter series with a per-item symbol + colour driven
  // by the backend's event_shape / event_color_hex.
  const singletons = clusters
    .filter((c) => c.events.length === 1)
    .map((c) => c.events[0]);
  const singletonSeries =
    singletons.length > 0
      ? [
          {
            name: "_events",
            type: "scatter" as const,
            symbolSize: 14,
            data: singletons.map((e) => ({
              value: [e.date, 0],
              symbol: eventShapeSymbol(e.shape),
              itemStyle: { color: e.colorHex },
              event: e,
            })),
          },
        ]
      : [];

  // Multi-event clusters: grey circle with a white count label.
  const groupClusters = clusters.filter((c) => c.events.length > 1);
  const clusterSeries =
    groupClusters.length > 0
      ? [
          {
            name: "_clusters",
            type: "scatter" as const,
            symbol: "circle" as const,

            symbolSize: (_v: unknown, p: DefaultLabelFormatterCallbackParams) =>
              Math.min(14 + (p.data as { count: number }).count * 3, 36),
            itemStyle: { color: "#475569" },
            label: {
              show: true,

              formatter: (p: DefaultLabelFormatterCallbackParams) =>
                String((p.data as { count: number }).count),
              color: "#fff",
              fontWeight: "bold" as const,
              fontSize: 10,
            },
            data: groupClusters.map((c) => ({
              value: [new Date(c.centerMs).toISOString(), 0],
              count: c.events.length,
              events: c.events,
            })),
          },
        ]
      : [];

  return {
    animation: false,
    grid: {
      left: GRID_LEFT,
      right: GRID_RIGHT,
      top: 8,
      bottom: 24,
      ...FIXED_GUTTERS,
    },
    axisPointer: {
      triggerOn: "click",
      label: { show: false },
      triggerTooltip: false,
    },
    tooltip: {
      trigger: "item",
      triggerOn: "mousemove",
      confine: true,
      // Descriptions are free text: without a cap and a wrap the tooltip grows
      // as one endless line and runs off the viewport.
      ...tooltipTheme(
        "max-width:320px;white-space:normal;overflow-wrap:anywhere;",
      ),
      formatter(params: TooltipComponentFormatterCallbackParams) {
        const p = (Array.isArray(params) ? params[0] : params) as unknown as {
          data?: unknown;
        };
        const d = p.data;
        if (d !== null && typeof d === "object" && !Array.isArray(d)) {
          if ("events" in d) {
            const rows = (d as { events: ChartEvent[] }).events.map(
              eventRowHtml,
            );
            return `<div>${rows.join("")}</div>`;
          }
          if ("event" in d) {
            return eventRowHtml((d as { event: ChartEvent }).event);
          }
        }
        return "";
      },
    },
    xAxis: {
      type: "time",
      position: "bottom",
      min: timeRange?.min,
      max: timeRange?.max,
      axisLine: { show: true },
      axisTick: { show: true },
      axisLabel: { hideOverlap: true, fontSize: 10 },
      splitLine: { show: false },
      axisPointer: rulerAxisPointer(false),
    },
    yAxis: { type: "value", min: -1, max: 1, show: false },
    dataZoom: [{ type: "inside", xAxisIndex: 0, disabled: true }],
    series: [...singletonSeries, ...clusterSeries],
  };
}

// Shared x-axis domain for every chart so timelines and the synced ruler
// align even when individual charts have narrower data. `extraDates` folds in
// event timestamps — an event newer than the last measurement would otherwise
// be clipped past the axis max with no way to reach it.
type ChartTimeRange = { min: string; max: string } | null;

export function computeChartTimeRange(
  plots: (PlotData | null)[],
  extraDates: string[] = [],
): ChartTimeRange {
  let min = Infinity;
  let max = -Infinity;
  const include = (timestamp: string) => {
    const t = new Date(timestamp).getTime();
    if (t < min) min = t;
    if (t > max) max = t;
  };
  for (const plot of plots) {
    for (const trace of plot?.timeseries?.traces ?? []) {
      for (const pt of trace.points) include(pt.timestamp);
    }
  }
  for (const date of extraDates) include(date);
  if (!isFinite(min)) return null;
  return { min: new Date(min).toISOString(), max: new Date(max).toISOString() };
}

export function buildZoomBarOption(min: string, max: string): EChartsOption {
  return {
    animation: false,
    grid: {
      left: GRID_LEFT,
      right: GRID_RIGHT,
      top: 4,
      bottom: 42,
      ...FIXED_GUTTERS,
    },
    xAxis: { type: "time", min, max, show: false },
    yAxis: { show: false },
    dataZoom: [{ type: "slider", xAxisIndex: 0, bottom: 12, height: 28 }],
    series: [],
  };
}

// Zone-fill palette: translucent semantic status washes matching the
// warning/alert/danger ladder in main.css.
const colors = {
  riskSafe: "rgba(34, 197, 94, 0.15)", // Tailwind green-500 @ 15%
  riskLow: "rgba(234, 179, 8, 0.18)", // Tailwind yellow-500 @ 18%
  riskMedium: "rgba(249, 115, 22, 0.22)", // Tailwind orange-500 @ 22%
  riskHigh: "rgba(220, 38, 38, 0.25)", // Tailwind red-600 @ 25%
};

// Identical gutters on every chart (incl. events + zoom bars) so the time
// axes and the synced ruler align down the page. The left one fits the widest
// label the charts produce — a four-digit weight, ~30px — plus its tick margin
// and the rotated axis name outside that, without any of them leaving the
// canvas. Widening it here keeps every chart in step, which is the whole point.
const GRID_LEFT = 64;
const GRID_RIGHT = 48;

// ECharts 6 shrinks a grid to keep its axis labels inside the canvas, so a
// chart whose y values reach four digits ("1,800") ends up narrower than its
// neighbours and falls out of step with the events bar above it. `none` lifts
// that constraint: the gutters below are honoured exactly and a wide label
// simply reaches further left, which the charts render with overflow visible.
// Every grid on the page sets it, or the ones that didn't would drift instead.
const FIXED_GUTTERS = { outerBoundsMode: "none" } as const;

// One marker size for every data line on the page. The charts used to disagree
// (4, 5 and — on symptoms — 8), which reads as meaning something when stacked
// vertically. The AFib chart's scatter overlay is deliberately larger; its dots
// carry that chart's colour scale rather than just marking a reading.
const POINT_SIZE = 5;

// Distance from the axis line to its rotated name. Must clear the widest label
// plus its 8px tick margin — a four-digit label is ~30px — or the name ends up
// printed through the numbers.
const AXIS_NAME_GAP = 46;

function sharedGrid() {
  return {
    left: GRID_LEFT,
    right: GRID_RIGHT,
    top: 16,
    bottom: 72,
    ...FIXED_GUTTERS,
  };
}

// Native axis-pointer (ruler) config for a time x-axis. The grid that owns the
// ruler shows a dashed rulerInk line with a drag handle; handle-less grids
// (events bar, non-last symptom sub-grids) render their native line invisible
// and rely on the shared graphic ruler instead (see EChartsChart).
function rulerAxisPointer(withHandle: boolean) {
  return {
    show: true,
    type: "line" as const,
    snap: false,
    label: { show: false },
    handle: withHandle ? { show: true, size: 30, margin: 40 } : { show: false },
    triggerTooltip: false,
    lineStyle: withHandle
      ? { color: CHART_COLORS.rulerInk, width: 2, type: "dashed" as const }
      : { opacity: 0 },
  };
}

function sharedAxisPointer(): EChartsOption["axisPointer"] {
  return {
    link: [{ xAxisIndex: "all" }],
    // Pointer only moves on click or handle drag — never on mouse move.
    triggerOn: "click",
    // No value/date label follows the ruler — per-point hover tooltip is the
    // only place values are shown.
    label: { show: false },
    triggerTooltip: false,
  };
}

function sharedTimeXAxis(timeRange: ChartTimeRange): EChartsOption["xAxis"] {
  return {
    type: "time",
    min: timeRange?.min,
    max: timeRange?.max,
    axisLabel: { hideOverlap: true },
    // Keep the axis line, ticks and date labels pinned to the grid bottom even
    // when values go negative (DRI) — otherwise they snap to the y=0 line.
    axisLine: { onZero: false },
    splitLine: { show: false },
    axisPointer: rulerAxisPointer(true),
  };
}

function sharedDataZoom(): EChartsOption["dataZoom"] {
  return [{ type: "inside", xAxisIndex: 0, disabled: true }];
}

// Per-point tooltip: hover a marker to see date + series name + value (+ unit).
function pointTooltip(unit = "", decimals = 2): EChartsOption["tooltip"] {
  return {
    trigger: "item",
    triggerOn: "mousemove",
    confine: true,
    ...tooltipTheme(),
    formatter(params: TooltipComponentFormatterCallbackParams) {
      const p = (Array.isArray(params) ? params[0] : params) as unknown as {
        value?: [string, number];
        color?: string;
        seriesName?: string;
      };
      const v = p.value;
      if (!v) return "";
      const valStr = tooltipValue(v[1], decimals) + (unit ? " " + unit : "");
      return tooltipHtml(
        v[0],
        tooltipDot(p.color ?? "#999"),
        p.seriesName ?? "",
        valStr,
      );
    },
  };
}

// ---------------------------------------------------------------------------
// Chart 1: HeartCore primary (headline LVFP line on risk-zone washes). The
// DRI/LVFP tabs' primary chart is buildRollingMetricOption below.
// ---------------------------------------------------------------------------

// Clinical LVFP cutoffs for the risk-zone washes (mmHg): 0–15 green,
// 15–25 orange, 25–40 red.
const HEARTCORE_ZONE_ELEVATED = 15;
const HEARTCORE_ZONE_HIGH = 25;
const HEARTCORE_ZONE_MAX = 40;

export function buildHeartcoreOption(
  plot: PlotData | null,
  metricLabel: string,
  timeRange: ChartTimeRange,
): EChartsOption {
  const trace = plot?.timeseries?.traces[0];
  const seriesData = (trace?.points ?? []).map((p) => [p.timestamp, p.value]);
  // LVFP values only exist once the patient has 4+ measurements — below that
  // the BE emits no points, and the chart renders empty with an explanation.
  const hasData = seriesData.length > 0;

  const thresholdMarkLines = (plot?.thresholds ?? []).map((t) => ({
    yAxis: t.value,
    label: {
      formatter: `${t.threshold_type === ThresholdTypes.high ? "Upper" : "Lower"}\nlimit\n${t.value}`,
      lineHeight: 12,
    },
    lineStyle: { type: "dashed" as const, opacity: 0.5 },
  }));

  return {
    animation: false,
    grid: sharedGrid(),
    axisPointer: sharedAxisPointer(),
    tooltip: pointTooltip(),
    xAxis: sharedTimeXAxis(timeRange),
    yAxis: {
      type: "value",
      // The unit, not the metric — the section title already names the chart,
      // and plain number labels keep the plot as wide as the other charts
      // (long "N mmHg" labels made ECharts shrink the grid to fit them).
      name: "mmHg",
      nameLocation: "middle",
      nameGap: AXIS_NAME_GAP,
      // Fixed full zone range with labels only at the zone boundaries.
      min: 0,
      max: HEARTCORE_ZONE_MAX,
      axisLabel: {
        customValues: [
          0,
          HEARTCORE_ZONE_ELEVATED,
          HEARTCORE_ZONE_HIGH,
          HEARTCORE_ZONE_MAX,
        ],
      },
      // The zone edges act as the gridlines; default splits would sit at
      // 10/20/30 and clash with the labelled 15/25 boundaries.
      splitLine: { show: false },
    },
    // Centered explanation on the empty (< 4 measurements) chart.
    graphic: hasData
      ? undefined
      : [
          {
            type: "text" as const,
            left: "center",
            top: "middle",
            silent: true,
            style: {
              text: i18n._(
                msg`Not enough data available - at least 4 measurements are necessary`,
              ),
              fill: CHART_COLORS.rulerInk,
              font: "13px sans-serif",
            },
          },
        ],
    dataZoom: sharedDataZoom(),
    series: [
      {
        name: metricLabel,
        type: "line",
        showSymbol: true,
        symbolSize: POINT_SIZE,
        lineStyle: { width: 2, color: CHART_COLORS.gridInk },
        itemStyle: { color: CHART_COLORS.gridInk },
        data: seriesData,
        markLine:
          thresholdMarkLines.length > 0
            ? {
                silent: true,
                symbol: "none",
                z: THRESHOLD_Z, // render beneath the data line
                data: thresholdMarkLines,
              }
            : undefined,
        markArea: {
          silent: true,
          data: [
            [
              { yAxis: 0, itemStyle: { color: colors.riskSafe } },
              { yAxis: HEARTCORE_ZONE_ELEVATED },
            ],
            [
              {
                yAxis: HEARTCORE_ZONE_ELEVATED,
                itemStyle: { color: colors.riskMedium },
              },
              { yAxis: HEARTCORE_ZONE_HIGH },
            ],
            [
              {
                yAxis: HEARTCORE_ZONE_HIGH,
                itemStyle: { color: colors.riskHigh },
              },
              { yAxis: HEARTCORE_ZONE_MAX },
            ],
          ],
        },
      },
    ],
  };
}

// Length of the run of successive same-direction steps that earns a purple
// triangle on the rolling line — the QuickLook's indicator. The report payload
// carries no such flag (nothing in the schema marks it), so it is derived from
// the rolling points here.
const TREND_RUN = 4;

// The direction each metric flags, and what the QuickLook calls it. They differ
// because the two metrics run opposite ways — a falling DRI and a rising LVFP
// both mean the patient is getting worse — so each chart marks only the
// direction that's clinically bad for it, never both.
export type TrendRun = {
  direction: "down" | "up";
  /** Series name, and the title the marker's tooltip shows. */
  label: string;
};

export const DRI_TREND_RUN: TrendRun = {
  direction: "down",
  label: `${TREND_RUN}-step Continuous Decrease`,
};

export const LVFP_TREND_RUN: TrendRun = {
  direction: "up",
  label: `${TREND_RUN}-step Continuous Increase`,
};

// Marker geometry for the run triangles: [width, height], not a single number.
// ECharts draws both the built-in "triangle" and a path:// symbol into this
// box, so a taller-than-wide box is what makes them read as sharp pointers
// rather than squat blobs — no second path needed for the up direction.
const TREND_RUN_SYMBOL: [number, number] = [14, 21];
// How far the symbol's CENTRE sits from the point it annotates. Half the height
// (10.5) is inside the triangle, so this leaves ~8px of clear air between its
// tip and the data point — the same gap the smaller marker had.
const TREND_RUN_OFFSET = 19;

// Indices of the points that close a run of TREND_RUN successive steps in the
// given direction. A longer run flags every point past the fourth, so a
// sustained trend stays marked instead of showing one triangle at its start.
// Nulls and flat steps break the run.
export function trendRunIndices(
  points: TimeSeriesPoint[],
  direction: TrendRun["direction"],
): number[] {
  const flagged: number[] = [];
  let run = 0;
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1].value;
    const current = points[i].value;
    if (prev === null || current === null) {
      run = 0;
      continue;
    }
    const stepped = direction === "down" ? current < prev : current > prev;
    run = stepped ? run + 1 : 0;
    if (run >= TREND_RUN) flagged.push(i);
  }
  return flagged;
}

// Rolling-metric chart (DRI + LVFP primary charts): grey raw line, black
// 4-rolling median with arrowheads + interval "I", dashed thresholds. Traces
// match by the `Transform` facet, whose values double as display names.
type RollingMetricConfig = {
  rawTransform: string; // Transform facet value for the raw-measurements trace
  rollingTransform: string; // Transform facet value for the rolling-median trace
  trendRun: TrendRun; // which run of the rolling line earns purple triangles
  thresholds: { value: number; color: string }[];
};

export const DRI_ROLLING: RollingMetricConfig = {
  rawTransform: "Raw DRI",
  rollingTransform: "4-rolling DRI",
  trendRun: DRI_TREND_RUN,
  thresholds: [
    { value: 50, color: CHART_COLORS.thresholdCautionLight },
    { value: 30, color: CHART_COLORS.thresholdCautionDark },
  ],
};

export const LVFP_ROLLING: RollingMetricConfig = {
  rawTransform: "LVFP Value",
  rollingTransform: "4-rolling LVFP",
  trendRun: LVFP_TREND_RUN,
  thresholds: [
    { value: 18, color: CHART_COLORS.thresholdCautionLight },
    { value: 15, color: CHART_COLORS.thresholdCautionDark },
  ],
};

// Exposed so the stat-row builder shares the same facet matching.
export function rollingMetricPoints(
  plot: PlotData | null,
  config: RollingMetricConfig,
): { raw: TimeSeriesPoint[]; rolling: TimeSeriesPoint[] } {
  const traces = plot?.timeseries?.traces ?? [];
  return {
    raw: pointsByFacets(traces, { Transform: config.rawTransform }),
    rolling: pointsByFacets(traces, { Transform: config.rollingTransform }),
  };
}

export function buildRollingMetricOption(
  plot: PlotData | null,
  metricLabel: string,
  timeRange: ChartTimeRange,
  config: RollingMetricConfig,
): EChartsOption {
  const rawLabel = config.rawTransform;
  const rollingLabel = config.rollingTransform;
  const markersName = `${rollingLabel} markers`;
  // Threshold lines live on their own carrier series (not the rolling line) so
  // they render beneath both lines and, driven by both stat rows in the OR
  // sense, stay visible until BOTH raw and rolling are hidden. See DriLvfpChart.
  const thresholdsName = `${rollingLabel} thresholds`;

  const { raw, rolling } = rollingMetricPoints(plot, config);

  // Each raw-line point gets its own dot color derived from the point's
  // Seerlinq Measurement Quality Flag (grey / orange / red). The connecting
  // line stays grey — only the symbol picks up per-point colour.
  const rawData = raw.map((p) => ({
    value: [p.timestamp, p.value],
    itemStyle: {
      color: qualityFlagColor(
        p.channels?.["Seerlinq Measurement Quality Flag"],
      ),
    },
  }));
  const rollingData = rolling.map((p) => [p.timestamp, p.value]);

  // Drawn by the custom series below as an angled arrowhead plus an "I" error
  // bar. Dims: [timeMs, value, error_y] — ms timestamps keep api.coord
  // unambiguous on the time axis.
  const toMs = (ts: string) => new Date(ts).getTime();
  const markerData = rolling.map((p) => [
    toMs(p.timestamp),
    p.value,
    p.error_y ?? null,
  ]);

  // Raw values indexed by timestamp — used inside the renderer to leave a gap
  // in the I-bar hit-target where a raw dot falls inside the error range, so
  // hovering the raw dot triggers its own tooltip instead of the rolling one.
  const rawByTsMs = new Map<number, number>();
  for (const p of raw) {
    if (p.value !== null) rawByTsMs.set(toMs(p.timestamp), p.value);
  }

  // Purple triangles on the rolling points that close a run of consecutive
  // same-direction steps — down for DRI, up for LVFP (see TrendRun).
  const trendRun = config.trendRun;
  const trendRunData = trendRunIndices(rolling, trendRun.direction).map((i) => [
    rolling[i].timestamp,
    rolling[i].value,
  ]);

  return {
    animation: false,
    grid: sharedGrid(),
    axisPointer: sharedAxisPointer(),
    // Fires on the raw series (date + value, quality-flag dot) and on the
    // markers custom series (arrowhead or I-bar → "value ±error" with an "I"
    // glyph). The custom series stores a numeric ms timestamp in `value[0]`
    // (not an ISO string), so it's normalised before formatting.
    tooltip: {
      trigger: "item",
      triggerOn: "mousemove",
      confine: true,
      ...tooltipTheme(),
      formatter(params: TooltipComponentFormatterCallbackParams) {
        const p = (Array.isArray(params) ? params[0] : params) as unknown as {
          value?: unknown;
          color?: string;
          seriesName?: string;
        };
        const v = p.value;
        if (!Array.isArray(v)) return "";
        const isMarker = p.seriesName === markersName;
        const rawT = v[0];
        const err = v[2];
        const suffix =
          isMarker && typeof err === "number" ? ` ±${err.toFixed(2)}` : "";
        const displayName = isMarker ? rollingLabel : (p.seriesName ?? "");
        const dotColor = p.color ?? (isMarker ? CHART_COLORS.arrowInk : "#999");
        // Markers get an "I" glyph matching the on-chart error bar (vertical
        // stem + two end caps); everything else gets the standard round dot.
        const glyph = isMarker
          ? `<span style="display:inline-block;vertical-align:middle;margin-right:5px;line-height:0;"><svg width="8" height="12" viewBox="0 0 8 12" fill="none" stroke="${CHART_COLORS.arrowInk}" stroke-width="1.25" stroke-linecap="square"><line x1="4" y1="1" x2="4" y2="11"/><line x1="0.75" y1="1" x2="7.25" y2="1"/><line x1="0.75" y1="11" x2="7.25" y2="11"/></svg></span>`
          : tooltipDot(dotColor);
        return tooltipHtml(
          typeof rawT === "number" ? rawT : String(rawT),
          glyph,
          displayName,
          `${tooltipValue(v[1], 2)}${suffix}`,
        );
      },
    },
    legend: {
      show: false,
      data: [
        rawLabel,
        rollingLabel,
        markersName,
        thresholdsName,
        trendRun.label,
      ],
    },
    xAxis: sharedTimeXAxis(timeRange),
    yAxis: {
      type: "value",
      name: metricLabel,
      nameLocation: "middle",
      nameGap: AXIS_NAME_GAP,
    },
    dataZoom: sharedDataZoom(),
    series: [
      {
        // Raw measurements: connecting grey line with per-point dots coloured
        // by the point's Seerlinq Measurement Quality Flag (baked into rawData).
        name: rawLabel,
        type: "line" as const,
        showSymbol: true,
        symbolSize: POINT_SIZE,
        lineStyle: { width: 1.5, color: CHART_COLORS.neutralMuted },
        itemStyle: { color: CHART_COLORS.neutralMuted },
        data: rawData,
        z: 1,
      },
      {
        // Rolling median line. No dots — arrowheads/error bars come from the
        // custom markers series below, which also fires the hover events.
        name: rollingLabel,
        type: "line" as const,
        showSymbol: false,
        lineStyle: { width: 2, color: CHART_COLORS.arrowInk },
        itemStyle: { color: CHART_COLORS.arrowInk },
        data: rollingData,
        z: 2,
      },
      {
        // Empty carrier for the baseline + threshold markLines: renders beneath
        // both lines and, with both stat rows pointing at it, hides only once
        // BOTH raw and rolling are hidden.
        name: thresholdsName,
        type: "line" as const,
        data: [],
        silent: true,
        z: THRESHOLD_Z,
        markLine: {
          silent: true,
          symbol: "none",
          z: THRESHOLD_Z,
          label: { lineHeight: 12 },
          data: [
            {
              yAxis: 0,
              lineStyle: {
                type: "solid" as const,
                color: CHART_COLORS.neutralMuted,
                width: 1,
              },
              label: { show: false },
            },
            // Dashed threshold lines (colour per config) with value labels.
            ...config.thresholds.map((t) => ({
              yAxis: t.value,
              lineStyle: { type: "dashed" as const, color: t.color, width: 2 },
              label: { formatter: `${t.value}`, color: t.color },
            })),
          ],
        },
      },
      {
        // Per-point arrowheads (angled along the incoming segment, in screen
        // space so unequal x/y scales don't skew them) plus "I" error bars.
        // Pixel sizes keep a fixed size through zoom; clipped to the grid.
        name: markersName,
        type: "custom" as const,
        z: 3,
        // Not silent — the arrowhead polygons are what the mouse hits to
        // trigger the "value ± error_y" tooltip. Individual I-bar line shapes
        // stay silent below so the thin bars don't emit tooltips of their own.
        silent: false,
        emphasis: { disabled: true },
        clip: true,
        encode: { x: 0, y: 1 },
        data: markerData,
        renderItem: (
          params: CustomSeriesRenderItemParams,
          api: CustomSeriesRenderItemAPI,
        ): CustomSeriesRenderItemReturn => {
          const idx = params.dataIndex;
          const t = api.value(0);
          const vRaw = api.value(1);
          const v = typeof vRaw === "number" ? vRaw : Number(vRaw);
          const empty = { type: "group" as const, children: [] };
          if (!Number.isFinite(v)) return empty;
          const [x, y] = api.coord([t, v]);
          const children: NonNullable<CustomSeriesRenderItemReturn>[] = [];

          // "I" error bar. The 1px strokes stay silent; an invisible wider
          // rect on top is the hover target so the tooltip fires anywhere
          // over the bar, not just the arrowhead.
          const dRaw = api.value(2);
          const delta = typeof dRaw === "number" ? dRaw : NaN;
          if (Number.isFinite(delta) && delta > 0) {
            const yUp = api.coord([t, v + delta])[1];
            const yLo = api.coord([t, v - delta])[1];
            const cap = 4;
            const style = { stroke: CHART_COLORS.arrowInk, lineWidth: 1 };
            children.push(
              {
                type: "line",
                silent: true,
                shape: { x1: x, y1: yLo, x2: x, y2: yUp },
                style,
              },
              {
                type: "line",
                silent: true,
                shape: { x1: x - cap, y1: yUp, x2: x + cap, y2: yUp },
                style,
              },
              {
                type: "line",
                silent: true,
                shape: { x1: x - cap, y1: yLo, x2: x + cap, y2: yLo },
                style,
              },
            );

            // Transparent hit-target over the I-bar. If a raw point falls
            // inside the ± range, split the rect around it — otherwise the
            // dot's own hit box gets buried and hovering it shows the
            // rolling tooltip.
            const rectX = x - cap - 1;
            const rectW = 2 * cap + 2;
            const RAW_DOT_HALF = 4; // half POINT_SIZE, rounded up, + slop
            const rawVal = rawByTsMs.get(Number(t));
            const rawInside =
              typeof rawVal === "number" &&
              rawVal >= v - delta &&
              rawVal <= v + delta;

            if (rawInside) {
              const yRaw = api.coord([t, rawVal])[1];
              const topBottom = yRaw - RAW_DOT_HALF;
              const bottomTop = yRaw + RAW_DOT_HALF;
              // Upper piece (yUp → just above raw dot).
              if (topBottom > yUp - 1) {
                children.push({
                  type: "rect",
                  shape: {
                    x: rectX,
                    y: yUp - 1,
                    width: rectW,
                    height: topBottom - (yUp - 1),
                  },
                  style: { fill: "rgba(0,0,0,0)" },
                });
              }
              // Lower piece (just below raw dot → yLo).
              if (bottomTop < yLo + 1) {
                children.push({
                  type: "rect",
                  shape: {
                    x: rectX,
                    y: bottomTop,
                    width: rectW,
                    height: yLo + 1 - bottomTop,
                  },
                  style: { fill: "rgba(0,0,0,0)" },
                });
              }
            } else {
              children.push({
                type: "rect",
                shape: {
                  x: rectX,
                  y: yUp - 1,
                  width: rectW,
                  height: yLo - yUp + 2,
                },
                style: { fill: "rgba(0,0,0,0)" },
              });
            }
          }

          // Arrowhead angled along the segment from the previous point, tip here.
          const prev = idx > 0 ? markerData[idx - 1] : null;
          const prevV = prev && typeof prev[1] === "number" ? prev[1] : null;
          if (prev && prevV != null) {
            const [px, py] = api.coord([prev[0], prevV]);
            const angle = Math.atan2(y - py, x - px);
            const len = 9;
            const half = 4.5;
            const bx = x - len * Math.cos(angle);
            const by = y - len * Math.sin(angle);
            const nx = Math.sin(angle);
            const ny = -Math.cos(angle);
            children.push({
              type: "polygon",
              shape: {
                points: [
                  [x, y],
                  [bx + half * nx, by + half * ny],
                  [bx - half * nx, by - half * ny],
                ],
              },
              style: { fill: CHART_COLORS.arrowInk },
            });
          }

          return { type: "group", children };
        },
      },
      // Continuous-run triangles, offset off the point they annotate so they
      // never sit on top of the line or its arrowheads. Each points back at its
      // point: a down-triangle above a falling run, an up-triangle below a
      // rising one.
      ...(trendRunData.length > 0
        ? [
            {
              name: trendRun.label,
              type: "scatter" as const,
              symbol:
                trendRun.direction === "down" ? TRIANGLE_DOWN_PATH : "triangle",
              symbolSize: TREND_RUN_SYMBOL,
              symbolOffset: [
                0,
                trendRun.direction === "down"
                  ? -TREND_RUN_OFFSET
                  : TREND_RUN_OFFSET,
              ],
              itemStyle: { color: CHART_COLORS.trendRunPurple },
              data: trendRunData,
              z: 4,
            },
          ]
        : []),
    ] as EChartsOption["series"],
  };
}

// ---------------------------------------------------------------------------
// Chart 2: Heart Rate — one line per algorithm source × PPG condition, on every
// tab, with only the default pair shown until the line selector opts others in.
// ---------------------------------------------------------------------------

// Fixed hue per known heart-rate algorithm, so a source keeps its colour across
// reports and tabs instead of drifting with trace order. Colour only: which line
// starts visible is decided by HR_DEFAULT_SOURCE/HR_DEFAULT_CONDITION in
// heartRateLines, and sources missing from this list fall through to the palette
// there. `color` is a getter so QPPG's theme-aware arrowInk resolves at access
// time rather than freezing its light-mode value at module load.
const HEART_SOURCES: { label: string; readonly color: string }[] = [
  {
    label: "Berry/fake Berry Data",
    get color() {
      return CHART_COLORS.bpPink;
    },
  },
  {
    label: "Seerlinq/EMD/IR",
    get color() {
      return CHART_COLORS.brandSecondary;
    },
  },
  {
    label: "Seerlinq/QPPG/beats/IR",
    get color() {
      return CHART_COLORS.arrowInk;
    },
  },
  {
    label: "Seerlinq/er/beats/IR",
    get color() {
      return cssVar("--color-blue-600", "#2563eb");
    },
  },
];

// Line palette by trace order, for sources we have no fixed colour for. A
// function so the third slot's theme-aware arrowInk reads fresh instead of
// freezing at module load.
export function hrConditionPalette(): readonly string[] {
  return [
    CHART_COLORS.bpPink,
    CHART_COLORS.brandSecondary,
    CHART_COLORS.arrowInk,
    cssVar("--color-blue-600", "#2563eb"),
  ];
}

// Prefers the `PPG Condition` facet so labels stay short ("Standing"); falls
// back to the raw `label` when facets are missing. Exported under its SpO2
// alias below — the heart rate chart names its lines via heartRateLineLabel,
// which also carries the source.
//
// The fallback stays as the BE composed it (it prepends the patient id, e.g.
// "1001, Condition 1") rather than being rewritten — every report shape the app
// handles carries the facet, so it is unreachable in practice, and pattern-
// matching free-form backend text to relabel it would be worse than the gap.
function hrConditionDisplayName(t: TimeSeriesTrace): string {
  const condition = t.facets?.["PPG Condition"];
  return condition ? ppgConditionText(condition) : t.label;
}

// The same identity, unlocalized: what a stat row keys its toggle on, so a
// locale switch doesn't read as a different line and reset the selection.
function hrConditionKey(t: TimeSeriesTrace): string {
  return t.facets?.["PPG Condition"] ?? t.label;
}

// One line per algorithm source × PPG condition. Only the default pair starts
// visible; the rest are opt-in from the section's line selector, which is why
// this replaced the old chart-per-condition split. Shared with the stat columns
// (see HeartRateChart) so the chart never draws a line the legend doesn't list.
export type HeartRateLine = {
  label: string;
  /** Raw source × condition — the ECharts series name and the toggle identity.
   *  Never display text: `label` is the localized twin. */
  key: string;
  /** The algorithm's hue, muted for condition 2 — see heartRateLines. */
  color: string;
  points: TimeSeriesPoint[];
  visible: boolean;
};

// The line clinical readers want by default: Seerlinq's QPPG algorithm on
// condition 1, named for its role rather than its algorithm.
// Both are compared against raw backend facet values, so neither is display
// text — the condition is shown as "Standing", but must not be renamed here.
const HR_DEFAULT_SOURCE = "Seerlinq/QPPG/beats/IR";
const HR_DEFAULT_CONDITION = "Condition 1";
export const HR_DEFAULT_LABEL = "HR - Default";

// Built from the plot's own traces rather than from HEART_SOURCES: the DRI
// report facets heart rate by `Source` × `PPG Condition`, while the LVFP report
// carries condition only, and both have to land in this one chart.
export function heartRateLines(hrPlot: PlotData | null): HeartRateLine[] {
  const palette = hrConditionPalette();
  const traces = (hrPlot?.timeseries?.traces ?? []).filter((t) =>
    t.points.some((p) => p.value !== null),
  );

  // One base hue per algorithm, assigned on first sight, so both of an
  // algorithm's conditions read as the same line family. Sources the app has a
  // colour for keep it; the rest take palette slots in order of appearance.
  const baseColors = new Map<string, string>();
  const baseColorFor = (source: string | undefined): string => {
    const key = source ?? "";
    const known = baseColors.get(key);
    if (known) return known;
    const color =
      HEART_SOURCES.find((s) => s.label === source)?.color ??
      palette[baseColors.size % palette.length];
    baseColors.set(key, color);
    return color;
  };

  // Condition 1 (and unfaceted traces) own the algorithm's colour; condition 2
  // takes a muted version of it. Distinguishing the pair by marker shape
  // instead — a square against a circle — was unreadable exactly where it
  // mattered, at the points where the two lines cross.
  const isLeadCondition = (t: TimeSeriesTrace) => {
    const condition = t.facets?.["PPG Condition"];
    return condition === undefined || condition === HR_DEFAULT_CONDITION;
  };

  const lines = traces.map((t) => {
    const source = t.facets?.["Source"];
    const condition = t.facets?.["PPG Condition"];
    const isDefault =
      source === HR_DEFAULT_SOURCE && condition === HR_DEFAULT_CONDITION;
    const base = baseColorFor(source);
    return {
      label: isDefault
        ? HR_DEFAULT_LABEL
        : heartRateLineLabel(t, ppgConditionText(condition)),
      key: isDefault
        ? HR_DEFAULT_LABEL
        : heartRateLineLabel(t, condition ?? ""),
      color: isLeadCondition(t) ? base : mutedShade(base),
      points: t.points,
      visible: isDefault,
    };
  });

  // No QPPG/condition-1 trace in this report — lead with the first condition-1
  // line, or just the first line, so the chart is never empty on mount.
  if (lines.length > 0 && !lines.some((l) => l.visible)) {
    const lead = lines[Math.max(traces.findIndex(isLeadCondition), 0)];
    lead.visible = true;
    // Reports without a `Source` facet (LVFP's condition-only traces, the
    // HeartCore report's single trace) offer no algorithm to name, so the lead
    // line takes the role name too rather than a patient-id-prefixed label.
    if (!traces.some((t) => t.facets?.["Source"])) {
      lead.label = HR_DEFAULT_LABEL;
      lead.key = HR_DEFAULT_LABEL;
    }
  }
  // Default first so it heads the selector; the rest keep report order.
  return lines.sort((a, b) => Number(b.visible) - Number(a.visible));
}

// "<source>, Standing" when both facets are present, otherwise whichever one is
// — the BE prepends the patient id to `label`, so that's the last resort.
// `condition` is passed in rather than read here so the same shape builds both
// the display label (localized) and the series key (raw facet value).
// `||` rather than `??`: ppgConditionText returns "" for a missing facet.
function heartRateLineLabel(t: TimeSeriesTrace, condition: string): string {
  const source = t.facets?.["Source"];
  if (source && condition) return `${source}, ${condition}`;
  return source || condition || t.label;
}

// The single Heart Rate chart, on every tab.
export function buildHeartRateOption(
  hrPlot: PlotData | null,
  timeRange: ChartTimeRange,
): EChartsOption {
  const lines = heartRateLines(hrPlot);
  // Reference lines (e.g. 50 + 110 bpm) come from the plot JSON; they live on
  // the REF_LINES_SERIES carrier so they hide only when every source line is
  // hidden (see the constant's comment) and draw beneath them via THRESHOLD_Z.
  const refMark = referenceLinesMark(
    getReferenceLines(hrPlot),
    CHART_COLORS.thresholdNeutral,
    (rl) => rl.label ?? `${rl.value} bpm`,
  );

  return {
    animation: false,
    grid: sharedGrid(),
    axisPointer: sharedAxisPointer(),
    legend: {
      show: false,
      data: [
        ...lines.map((l) => l.label),
        ...(refMark ? [REF_LINES_SERIES] : []),
      ],
      // Start the opt-in lines hidden to avoid a mount flash; the section's line
      // selector (ChartSection) keeps this in sync afterwards. The reference
      // lines ride along with whichever line is showing.
      selected: {
        ...Object.fromEntries(lines.map((l) => [l.label, l.visible])),
        ...(refMark ? { [REF_LINES_SERIES]: true } : {}),
      },
    },
    tooltip: pointTooltip("bpm", 0),
    xAxis: sharedTimeXAxis(timeRange),
    yAxis: {
      type: "value",
      name: "bpm",
      nameLocation: "middle",
      nameGap: AXIS_NAME_GAP,
    },
    dataZoom: sharedDataZoom(),
    series: [
      ...lines.map((l) => ({
        name: l.label,
        type: "line" as const,
        showSymbol: true,
        symbolSize: POINT_SIZE,
        lineStyle: { width: 2 },
        itemStyle: { color: l.color },
        data: l.points
          .filter((p) => p.value !== null)
          .map((p) => [p.timestamp, p.value]),
      })),
      ...(refMark
        ? [
            {
              name: REF_LINES_SERIES,
              type: "line" as const,
              data: [],
              silent: true,
              z: THRESHOLD_Z,
              markLine: refMark,
            },
          ]
        : []),
    ],
  };
}

// ---------------------------------------------------------------------------
// Chart 2b: AFib probability (DRI tab, AFIB permission). Grey lines carrying
// dots coloured on a green → red gradient by the probability they sit at.
// ---------------------------------------------------------------------------

export type AfibLine = {
  /** Series name of the grey line, and the summary column's header. */
  label: string;
  /** Series name of the gradient-dot overlay drawn on top of that line. */
  dotsSeries: string;
  /** Raw condition — the toggle identity, unlike the localized `label`. */
  key: string;
  color: string;
  /** Values rescaled to percent — see afibPercentFactor. */
  points: TimeSeriesPoint[];
  visible: boolean;
};

// Column name for a payload that carries one unfaceted probability series (see
// afibSourceSeries) — there's no condition to name it after, and the section
// title already says "AFib probability".
const AFIB_SINGLE_LABEL = "Probability";

// Line shades, condition 1 first. The dots carry all of this chart's colour, so
// the lines only have to stay distinguishable from one another — hence grey,
// one step apart, rather than another pair of categorical hues.
function afibLinePalette(): readonly string[] {
  return isDarkMode()
    ? ["#d4d4d8", "#71717a", "#a1a1aa"]
    : ["#52525b", "#a1a1aa", "#71717a"];
}

// Dot ramp: 0 % dark green → lime → yellow → orange → 100 % red. ECharts spaces
// these evenly across the visualMap range and interpolates between them, so the
// dots read as a continuous gradient rather than five bands. Dark mode lifts the
// darkest stops, which otherwise sink into the plot background.
function afibDotRamp(): string[] {
  return isDarkMode()
    ? ["#16a34a", "#84cc16", "#facc15", "#fb923c", "#ef4444"]
    : ["#15803d", "#65a30d", "#eab308", "#f97316", "#dc2626"];
}

// Distinct series name for a line's dot overlay. The two always toggle together
// (AfibChart lists both in `seriesNames`) and the tooltip maps this back to the
// line's own label.
function afibDotsSeries(label: string): string {
  return `${label} dots`;
}

// Where AFib probability lives in the payload, most specific first: the master
// report's own `afib` section, one trace per PPG condition; failing that a bare
// `afib_probability` array, which the BE hangs off the AFib plot or — as in the
// DRI QuickLook — off the Heart rate plot. Same chart whichever shape arrives.
function afibSourceSeries(
  afibPlot: PlotData | null,
  hrPlot: PlotData | null,
): { label: string; key: string; points: TimeSeriesPoint[] }[] {
  const traces = (afibPlot?.timeseries?.traces ?? []).filter((t) =>
    t.points.some((p) => p.value !== null),
  );
  if (traces.length > 0) {
    return traces.map((t) => ({
      // Same facet naming as heart rate: "Condition 1" / "Condition 2", shown
      // as its localized posture. `key` keeps the raw one for the toggle.
      label: heartRateLineLabel(
        t,
        ppgConditionText(t.facets?.["PPG Condition"]),
      ),
      key: heartRateLineLabel(t, t.facets?.["PPG Condition"] ?? ""),
      points: t.points,
    }));
  }
  for (const points of [afibPlot?.afib_probability, hrPlot?.afib_probability]) {
    if (points?.some((p) => p.value !== null)) {
      return [{ label: AFIB_SINGLE_LABEL, key: AFIB_SINGLE_LABEL, points }];
    }
  }
  return [];
}

/** Whether this report has any AFib probability to plot, in either shape. */
export function hasAfibData(
  afibPlot: PlotData | null,
  hrPlot: PlotData | null,
): boolean {
  return afibSourceSeries(afibPlot, hrPlot).length > 0;
}

// The axis, the dot ramp and the summary numbers all work in percent, but the
// BE sends the probability as a 0–1 fraction (as the DRI QuickLook's AFib plot
// does). A reading above 1 can only be a percentage already, so that — not a
// hardcoded unit — is what decides whether to rescale.
//
// The one shape this gets wrong is a *percent* payload whose every value is
// below 1 %: with nothing above 1 to give it away it is read as a fraction and
// multiplied by 100, so 0.4 % lands at 40 on the axis with an orange dot. That
// is the reassuring case rendered as the alarming one, so if the BE ever starts
// sending percent, pin the unit here and delete the guess — don't extend it.
function afibPercentFactor(series: { points: TimeSeriesPoint[] }[]): number {
  for (const s of series) {
    for (const p of s.points) {
      if (p.value !== null && p.value > 1) return 1;
    }
  }
  return 100;
}

// Shared with the summary columns (see AfibChart) so the chart never draws a
// line the legend doesn't list, exactly as heartRateLines does for heart rate.
export function afibProbabilityLines(
  afibPlot: PlotData | null,
  hrPlot: PlotData | null,
): AfibLine[] {
  const series = afibSourceSeries(afibPlot, hrPlot);
  const palette = afibLinePalette();
  const factor = afibPercentFactor(series);
  return series.map((s, i) => ({
    label: s.label,
    key: s.key,
    dotsSeries: afibDotsSeries(s.label),
    color: palette[i % palette.length],
    points:
      factor === 1
        ? s.points
        : s.points.map((p) =>
            typeof p.value === "number" ? { ...p, value: p.value * factor } : p,
          ),
    // Condition 1 only by default; the rest are opt-in from the line selector.
    visible: i === 0,
  }));
}

export function buildAfibOption(
  afibPlot: PlotData | null,
  hrPlot: PlotData | null,
  timeRange: ChartTimeRange,
): EChartsOption {
  const lines = afibProbabilityLines(afibPlot, hrPlot);
  if (lines.length === 0) return { animation: false };

  // Each line contributes [line, dots] below, so the dot overlays land on the
  // odd indices — the visualMap has to repaint those and leave the lines grey.
  const dotSeriesIndices = lines.map((_, i) => i * 2 + 1);
  const lineLabelByDots = new Map(lines.map((l) => [l.dotsSeries, l.label]));

  return {
    animation: false,
    grid: sharedGrid(),
    axisPointer: sharedAxisPointer(),
    visualMap: {
      type: "continuous",
      show: false,
      dimension: 1, // the probability, not the timestamp
      seriesIndex: dotSeriesIndices,
      min: 0,
      max: 100,
      inRange: { color: afibDotRamp() },
    },
    legend: {
      show: false,
      data: lines.flatMap((l) => [l.label, l.dotsSeries]),
      // Opt-in lines start hidden to avoid a mount flash; the section's line
      // selector keeps this in sync afterwards. A line and its dots are one
      // logical series and are always selected together.
      selected: Object.fromEntries(
        lines.flatMap((l) => [
          [l.label, l.visible],
          [l.dotsSeries, l.visible],
        ]),
      ),
    },
    tooltip: {
      trigger: "item",
      triggerOn: "mousemove",
      confine: true,
      ...tooltipTheme(),
      formatter(params: TooltipComponentFormatterCallbackParams) {
        const p = (Array.isArray(params) ? params[0] : params) as unknown as {
          value?: [string, number];
          color?: string;
          seriesName?: string;
        };
        const v = p.value;
        if (!v) return "";
        const name = p.seriesName ?? "";
        // The dot overlay is what the pointer hits, and its params carry the
        // visualMap-resolved colour — so the glyph matches the dot on screen.
        // A decimal here, none in the summary: hovering is where you go for the
        // precision the rounded column drops.
        return tooltipHtml(
          v[0],
          tooltipDot(p.color ?? CHART_COLORS.neutralMuted),
          lineLabelByDots.get(name) ?? name,
          `${tooltipValue(v[1], 1)} %`,
        );
      },
    },
    xAxis: sharedTimeXAxis(timeRange),
    yAxis: {
      type: "value",
      name: "%",
      nameLocation: "middle",
      nameGap: AXIS_NAME_GAP,
      // Fixed full range, not auto-scaled: the dot colours are absolute (0 %
      // green → 100 % red), so a zoomed-in axis would make a flat low-risk
      // series look like it swings wildly while every dot stayed green.
      min: 0,
      max: 100,
    },
    dataZoom: sharedDataZoom(),
    series: lines.flatMap((l) => {
      const data = l.points
        .filter((p) => p.value !== null)
        .map((p) => [p.timestamp, p.value]);
      return [
        {
          name: l.label,
          type: "line" as const,
          // Symbols come from the scatter overlay below instead: a line's own
          // symbols would all take the line colour, and the visualMap can't
          // repaint just the symbols of a line series.
          showSymbol: false,
          lineStyle: { width: 2, color: l.color },
          itemStyle: { color: l.color },
          data,
          z: 1,
        },
        {
          name: l.dotsSeries,
          type: "scatter" as const,
          symbolSize: 7,
          data,
          z: 2,
        },
      ];
    }) as EChartsOption["series"],
  };
}

// ---------------------------------------------------------------------------
// Chart 3: Blood Pressure (Systolic + Diastolic)
// ---------------------------------------------------------------------------

export function buildBloodPressureOption(
  plot: PlotData | null,
  timeRange: ChartTimeRange,
): EChartsOption {
  const traces = plot?.timeseries?.traces ?? [];
  const findTrace = (measurement: string) =>
    pointsByFacets(traces, { "Measurement Type": measurement });

  // JSON reference lines carry `applies_to: { "Measurement Type": ... }` so
  // each threshold snaps onto its own trace; getReferenceLines filters by
  // facets.
  const sysRefs = getReferenceLines(plot, {
    "Measurement Type": "Blood Pressure Systolic",
  });
  const diaRefs = getReferenceLines(plot, {
    "Measurement Type": "Blood Pressure Diastolic",
  });
  const sysMark = referenceLinesMark(
    sysRefs,
    CHART_COLORS.bpPink,
    (rl) => rl.label ?? `Systolic\n${rl.value}`,
  );
  const diaMark = referenceLinesMark(
    diaRefs,
    CHART_COLORS.brandSecondary,
    (rl) => rl.label ?? `Diastolic\n${rl.value}`,
  );

  return {
    animation: false,
    grid: sharedGrid(),
    axisPointer: sharedAxisPointer(),
    legend: { show: false, data: ["Systolic", "Diastolic"] },
    tooltip: pointTooltip("mmHg", 0),
    xAxis: sharedTimeXAxis(timeRange),
    yAxis: {
      type: "value",
      name: "mmHg",
      nameLocation: "middle",
      nameGap: AXIS_NAME_GAP,
    },
    dataZoom: sharedDataZoom(),
    series: [
      {
        name: "Systolic",
        type: "line",
        showSymbol: true,
        symbolSize: POINT_SIZE,
        lineStyle: { width: 2 },
        itemStyle: { color: CHART_COLORS.bpPink },
        data: findTrace("Blood Pressure Systolic").map((p) => [
          p.timestamp,
          p.value,
        ]),
        markLine: sysMark,
      },
      {
        name: "Diastolic",
        type: "line",
        showSymbol: true,
        symbolSize: POINT_SIZE,
        lineStyle: { width: 2 },
        itemStyle: { color: CHART_COLORS.brandSecondary },
        data: findTrace("Blood Pressure Diastolic").map((p) => [
          p.timestamp,
          p.value,
        ]),
        markLine: diaMark,
      },
    ],
  };
}

// ---------------------------------------------------------------------------
// Chart 4: Symptoms
// ---------------------------------------------------------------------------

// One stacked sub-grid per symptom, rendering only symptoms with data so the
// chart shrinks and the ruler handle sits under the lowest visible sub-grid.
const SYMPTOM_SUB_HEIGHT = 107;
const SYMPTOM_TOP = 8;
// Room for the divider rule below each sub-grid, plus air on both sides of it.
const SYMPTOM_GAP = 24;
// How far the divider sits below the sub-grid it closes. Clear of the bottom
// gridline, so it reads as a divider and not as that plot's base.
const SYMPTOM_DIVIDER_OFFSET = 8;
// Room below the last sub-grid for its axis labels + the ruler handle.
const SYMPTOM_BOTTOM_GAP = 72;

type SymptomDef = {
  label: string; // backend trace label (match key)
  name: string; // display / series name
  color: string;
  min: number;
  max: number;
  splitNumber?: number;
};

const SYMPTOM_DEFS: SymptomDef[] = [
  {
    label: "Shortness Of Breath",
    name: "Shortness of breath",
    color: CHART_COLORS.symptomsBlue,
    min: 1,
    max: 4,
    splitNumber: 3,
  },
  {
    label: "Fatigue Score",
    name: "Fatigue score",
    color: CHART_COLORS.symptomsPurple,
    min: 0,
    max: 10,
  },
  {
    label: "Perceived Health Change",
    name: "Perceived health change",
    color: CHART_COLORS.symptomsCyan,
    min: 1,
    max: 4,
    splitNumber: 3,
  },
];

function presentSymptoms(plot: PlotData | null) {
  const traces = plot?.timeseries?.traces ?? [];
  return SYMPTOM_DEFS.map((def) => {
    const points = pointsByFacets(traces, { "Symptom Name": def.label });
    return { def, data: points.map((p) => [p.timestamp, p.value]) };
  }).filter((s) => s.data.length > 0);
}

// Total chart height for the symptoms currently present (0 when none).
export function symptomsChartHeight(plot: PlotData | null): number {
  const n = presentSymptoms(plot).length;
  if (n === 0) return 0;
  return (
    SYMPTOM_TOP +
    SYMPTOM_SUB_HEIGHT * n +
    SYMPTOM_GAP * (n - 1) +
    SYMPTOM_BOTTOM_GAP
  );
}

// Where the rules between stacked symptoms sit, as px from the top of the
// chart: just below each sub-grid except the last. Mirrors the `grid` layout
// below, and is what ChartSection positions its divider elements with.
export function symptomDividerOffsets(plot: PlotData | null): number[] {
  const n = presentSymptoms(plot).length;
  return Array.from(
    { length: Math.max(n - 1, 0) },
    (_, i) =>
      SYMPTOM_TOP +
      (SYMPTOM_SUB_HEIGHT + SYMPTOM_GAP) * i +
      SYMPTOM_SUB_HEIGHT +
      SYMPTOM_DIVIDER_OFFSET,
  );
}

export function buildSymptomsOption(
  plot: PlotData | null,
  timeRange: ChartTimeRange,
): EChartsOption {
  const present = presentSymptoms(plot);
  const n = present.length;
  if (n === 0) return { animation: false };

  return {
    animation: false,
    axisPointer: sharedAxisPointer(),
    legend: { show: false, data: present.map((s) => s.def.name) },
    // Custom tooltip only so the dot takes the symptom's own colour rather
    // than the hovered series' resolved one, and the value loses its decimals.
    tooltip: {
      trigger: "item",
      triggerOn: "mousemove",
      confine: true,
      ...tooltipTheme(),
      formatter(params: TooltipComponentFormatterCallbackParams) {
        const p = (Array.isArray(params) ? params[0] : params) as unknown as {
          value?: [string, number];
          color?: string;
          seriesName?: string;
        };
        const v = p.value;
        if (!v) return "";
        const def = SYMPTOM_DEFS.find((d) => d.name === p.seriesName);
        return tooltipHtml(
          v[0],
          tooltipDot(def?.color ?? p.color ?? CHART_COLORS.neutralMuted),
          p.seriesName ?? "",
          tooltipValue(v[1], 0),
        );
      },
    },
    grid: present.map((_, i) => ({
      left: GRID_LEFT,
      right: GRID_RIGHT,
      top: SYMPTOM_TOP + (SYMPTOM_SUB_HEIGHT + SYMPTOM_GAP) * i,
      height: SYMPTOM_SUB_HEIGHT,
      ...FIXED_GUTTERS,
    })),
    // Only the last (bottom) sub-grid shows time labels and owns the ruler
    // handle; the others use a hidden native pointer and rely on the shared
    // graphic ruler lines (drawn on every grid but the last) to stay in sync.
    xAxis: present.map((_, i) => {
      const isLast = i === n - 1;
      return {
        type: "time",
        gridIndex: i,
        min: timeRange?.min,
        max: timeRange?.max,
        axisLabel: isLast ? { hideOverlap: true } : { show: false },
        axisTick: isLast ? undefined : { show: false },
        // Only the bottom sub-grid draws an axis line. The rules that separate
        // the stacked symptoms are DOM elements over the chart instead (see
        // symptomDividerOffsets) — an axis line can only span the plot area,
        // leaving the axis names beside it visually un-separated. onZero keeps
        // the line pinned to the grid bottom rather than to y=0.
        axisLine: isLast ? { onZero: false } : { show: false },
        splitLine: { show: false },
        axisPointer: rulerAxisPointer(isLast),
      };
    }),
    yAxis: present.map((s, i) => ({
      type: "value",
      gridIndex: i,
      name: s.def.name,
      nameLocation: "middle",
      nameGap: 28,
      min: s.def.min,
      max: s.def.max,
      ...(s.def.splitNumber ? { splitNumber: s.def.splitNumber } : {}),
    })),
    dataZoom: [
      { type: "inside", xAxisIndex: present.map((_, i) => i), disabled: true },
    ],
    series: present.map((s, i) => ({
      name: s.def.name,
      type: "line",
      xAxisIndex: i,
      yAxisIndex: i,
      showSymbol: true,
      symbolSize: POINT_SIZE,
      lineStyle: { width: 2 },
      itemStyle: { color: s.def.color },
      data: s.data,
    })),
  };
}

// ---------------------------------------------------------------------------
// Chart 5: Weight
// ---------------------------------------------------------------------------

export function buildWeightOption(
  plot: PlotData | null,
  timeRange: ChartTimeRange,
): EChartsOption {
  const trace = plot?.timeseries?.traces[0];
  const points = (trace?.points ?? []).map((p) => [p.timestamp, p.value]);

  return {
    animation: false,
    grid: sharedGrid(),
    axisPointer: sharedAxisPointer(),
    tooltip: pointTooltip("kg", 2),
    xAxis: sharedTimeXAxis(timeRange),
    yAxis: {
      type: "value",
      name: "kg",
      nameLocation: "middle",
      nameGap: AXIS_NAME_GAP,
    },
    dataZoom: sharedDataZoom(),
    series: [
      {
        name: "Weight",
        type: "line",
        showSymbol: true,
        symbolSize: POINT_SIZE,
        lineStyle: { width: 2 },
        itemStyle: { color: CHART_COLORS.brandPrimary },
        data: points,
      },
    ],
  };
}

// ---------------------------------------------------------------------------
// Chart 6: SpO2
// ---------------------------------------------------------------------------

// Shared with the LVFP HR chart so the same condition reads with the same
// colour. Alias, not a snapshot, so the arrowInk slot keeps tracking theme.
export const spo2ConditionPalette = hrConditionPalette;

// SpO₂ traces use the same `PPG Condition` facet naming as heart rate.
export const spo2TraceName = hrConditionDisplayName;

// ...and the same raw identity for their toggles. See hrConditionKey.
export const spo2TraceKey = hrConditionKey;

export function buildSpo2Option(
  plot: PlotData | null,
  timeRange: ChartTimeRange,
): EChartsOption {
  const traces = plot?.timeseries?.traces ?? [];
  const names = traces.map(spo2TraceName);
  const palette = spo2ConditionPalette();

  return {
    animation: false,
    grid: sharedGrid(),
    axisPointer: sharedAxisPointer(),
    tooltip: pointTooltip("%", 2),
    // As on the HR conditions chart, only the leading condition starts visible.
    legend: {
      show: false,
      data: names,
      selected: Object.fromEntries(names.map((n, i) => [n, i === 0])),
    },
    xAxis: sharedTimeXAxis(timeRange),
    yAxis: {
      type: "value",
      name: "SpO₂ %",
      nameLocation: "middle",
      nameGap: AXIS_NAME_GAP,
      // Soft lower bound: 85 by default, but never clip a lower reading —
      // severe desaturation is exactly the value that must stay visible.
      min: ({ min }) => Math.min(85, Math.floor(min)),
      max: 100,
    },
    dataZoom: sharedDataZoom(),
    series: traces.map((t, i) => {
      return {
        name: names[i],
        type: "line" as const,
        showSymbol: true,
        symbolSize: POINT_SIZE,
        lineStyle: { width: 2 },
        itemStyle: { color: palette[i % palette.length] },
        data: t.points
          .filter((p) => p.value !== null)
          .map((p) => [p.timestamp, p.value]),
      };
    }),
  };
}
